import { Node, Edge } from 'react-flow-renderer';
import dagre from 'dagre';
import { EmployeePosition } from './types';
import { LAYOUT_CONFIG, NODE_SIZES } from './constants';

export const getNodeDimensions = (
  position: EmployeePosition,
  displayStaffedPositions: boolean,
) => {
  const isMinimal =
    !displayStaffedPositions &&
    position.flair__Staffed__c &&
    !position.isCurrentPosition;
  return isMinimal ? NODE_SIZES.minimal : NODE_SIZES.normal;
};

export const isPositionSelectable = (position: EmployeePosition) => {
  return !position.flair__Staffed__c || position.isCurrentPosition;
};

export const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  if (nodes.length === 0) {
    return { nodes: [], edges };
  }

  const positions = nodes.map((node) => node.data.position);
  const treeComponents = buildPositionTree(positions, positions[0]?.Id || '');

  if (treeComponents.length <= 1) {
    const dagreGraph = new dagre.graphlib.Graph({ compound: true });
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph(LAYOUT_CONFIG);

    nodes.forEach((node) => {
      const { width, height } = getNodeDimensions(
        node.data.position,
        node.data.displayStaffedPositions,
      );
      dagreGraph.setNode(node.id, { width, height });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      const { width, height } = getNodeDimensions(
        node.data.position,
        node.data.displayStaffedPositions,
      );

      return {
        ...node,
        position: {
          x: nodeWithPosition.x - width / 2,
          y: nodeWithPosition.y - height / 2,
        },
      };
    });

    const alignedNodes = alignSiblingsTopEdges(layoutedNodes, positions);
    return { nodes: alignedNodes, edges };
  }

  const allLayoutedNodes: Node[] = [];
  const treeBounds: Array<{
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  }> = [];
  let currentXOffset = 0;
  const calculateOptimalSpacing = (treeNodes: Node[]): number => {
    const maxNodeWidth = Math.max(
      ...treeNodes.map(
        (node) =>
          getNodeDimensions(
            node.data.position,
            node.data.displayStaffedPositions,
          ).width,
      ),
    );
    return Math.max(120, maxNodeWidth * 0.8);
  };

  treeComponents.forEach((treeNodeIds, treeIndex) => {
    const treeNodes = nodes.filter((node) => treeNodeIds.includes(node.id));
    const treeEdges = edges.filter(
      (edge) =>
        treeNodeIds.includes(edge.source) && treeNodeIds.includes(edge.target),
    );

    if (treeNodes.length === 0) return;

    const optimalSpacing = calculateOptimalSpacing(treeNodes);

    const dagreGraph = new dagre.graphlib.Graph({ compound: true });
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
      ...LAYOUT_CONFIG,
      marginx: Math.max(LAYOUT_CONFIG.marginx, 100),
      marginy: Math.max(LAYOUT_CONFIG.marginy, 100),
    });

    treeNodes.forEach((node) => {
      const { width, height } = getNodeDimensions(
        node.data.position,
        node.data.displayStaffedPositions,
      );
      dagreGraph.setNode(node.id, { width, height });
    });

    treeEdges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedTreeNodes = treeNodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      const { width, height } = getNodeDimensions(
        node.data.position,
        node.data.displayStaffedPositions,
      );

      return {
        ...node,
        position: {
          x: nodeWithPosition.x - width / 2 + currentXOffset,
          y: nodeWithPosition.y - height / 2,
        },
      };
    });

    const alignedTreeNodes = alignSiblingsTopEdges(
      layoutedTreeNodes,
      positions,
    );

    if (alignedTreeNodes.length === 0) return;

    const treeBound = {
      minX: Math.min(...alignedTreeNodes.map((node) => node.position.x)),
      maxX: Math.max(
        ...alignedTreeNodes.map((node) => {
          const { width } = getNodeDimensions(
            node.data.position,
            node.data.displayStaffedPositions,
          );
          return node.position.x + width;
        }),
      ),
      minY: Math.min(...alignedTreeNodes.map((node) => node.position.y)),
      maxY: Math.max(
        ...alignedTreeNodes.map((node) => {
          const { height } = getNodeDimensions(
            node.data.position,
            node.data.displayStaffedPositions,
          );
          return node.position.y + height;
        }),
      ),
    };

    for (const existingBound of treeBounds) {
      const hasOverlap = !(
        treeBound.maxX < existingBound.minX ||
        treeBound.minX > existingBound.maxX ||
        treeBound.maxY < existingBound.minY ||
        treeBound.minY > existingBound.maxY
      );

      if (hasOverlap) {
        const additionalOffset =
          existingBound.maxX - treeBound.minX + optimalSpacing;
        alignedTreeNodes.forEach((node) => {
          node.position.x += additionalOffset;
        });
        treeBound.minX += additionalOffset;
        treeBound.maxX += additionalOffset;
        currentXOffset += additionalOffset;
      }
    }

    treeBounds.push(treeBound);
    allLayoutedNodes.push(...alignedTreeNodes);

    const treeWidth = treeBound.maxX - treeBound.minX;
    if (treeIndex < treeComponents.length - 1) {
      currentXOffset += treeWidth + optimalSpacing;
    }
  });

  const validateLayout = (layoutedNodes: Node[]) => {
    const nodePositions = layoutedNodes.map((node) => ({
      id: node.id,
      x: node.position.x,
      y: node.position.y,
      width: getNodeDimensions(
        node.data.position,
        node.data.displayStaffedPositions,
      ).width,
      height: getNodeDimensions(
        node.data.position,
        node.data.displayStaffedPositions,
      ).height,
    }));

    const overlaps: string[] = [];
    for (let i = 0; i < nodePositions.length; i++) {
      for (let j = i + 1; j < nodePositions.length; j++) {
        const nodeA = nodePositions[i];
        const nodeB = nodePositions[j];

        const hasOverlap = !(
          nodeA.x + nodeA.width <= nodeB.x ||
          nodeB.x + nodeB.width <= nodeA.x ||
          nodeA.y + nodeA.height <= nodeB.y ||
          nodeB.y + nodeB.height <= nodeA.y
        );

        if (hasOverlap) {
          overlaps.push(`Nodes ${nodeA.id} and ${nodeB.id} overlap`);
        }
      }
    }

    if (overlaps.length > 0) {
      console.warn(
        'Layout validation failed - overlapping nodes detected:',
        overlaps,
      );
    }

    return overlaps.length === 0;
  };

  validateLayout(allLayoutedNodes);
  return { nodes: allLayoutedNodes, edges };
};

const alignSiblingsTopEdges = (
  nodes: Node[],
  positions: EmployeePosition[],
): Node[] => {
  const siblingGroups = new Map<string, Node[]>();
  const rootNodes: Node[] = [];

  nodes.forEach((node) => {
    const position = positions.find((p) => p.Id === node.id);
    const parentId = position?.flair__Parent_Position__c;

    if (parentId) {
      if (!siblingGroups.has(parentId)) {
        siblingGroups.set(parentId, []);
      }
      siblingGroups.get(parentId)!.push(node);
    } else {
      rootNodes.push(node);
    }
  });

  const alignedNodes = [...nodes];

  siblingGroups.forEach((siblings) => {
    if (siblings.length <= 1) return;

    const minTopY = Math.min(...siblings.map((node) => node.position.y));

    siblings.forEach((sibling) => {
      const nodeIndex = alignedNodes.findIndex((n) => n.id === sibling.id);
      if (nodeIndex !== -1) {
        alignedNodes[nodeIndex] = {
          ...alignedNodes[nodeIndex],
          position: {
            ...alignedNodes[nodeIndex].position,
            y: minTopY,
          },
        };
      }
    });
  });

  return alignedNodes;
};

const detectCycles = (positions: EmployeePosition[]): string[] => {
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const cycles: string[] = [];

  const dfs = (positionId: string, path: string[]): boolean => {
    if (recursionStack.has(positionId)) {
      const cycleStart = path.indexOf(positionId);
      cycles.push(
        `Cycle detected: ${path
          .slice(cycleStart)
          .join(' -> ')} -> ${positionId}`,
      );
      return true;
    }

    if (visited.has(positionId)) return false;

    visited.add(positionId);
    recursionStack.add(positionId);

    const position = positions.find((p) => p.Id === positionId);
    if (position?.flair__Parent_Position__c) {
      const hasCycle = dfs(position.flair__Parent_Position__c, [
        ...path,
        positionId,
      ]);
      if (hasCycle) return true;
    }

    recursionStack.delete(positionId);
    return false;
  };

  positions.forEach((pos) => {
    if (!visited.has(pos.Id)) {
      dfs(pos.Id, []);
    }
  });

  return cycles;
};

export const buildPositionTree = (
  positions: EmployeePosition[],
  currentPositionId: string,
) => {
  const cycles = detectCycles(positions);
  if (cycles.length > 0) {
    console.warn('Position hierarchy cycles detected:', cycles);
  }

  const positionMap = new Map(positions.map((pos) => [pos.Id, pos]));
  const visited = new Set<string>();
  const treeComponents: string[][] = [];

  const findTreeRoot = (startId: string): string => {
    const visited = new Set<string>();
    let currentId = startId;

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const position = positionMap.get(currentId);
      if (!position?.flair__Parent_Position__c) {
        return currentId;
      }
      currentId = position.flair__Parent_Position__c;
    }

    return startId;
  };

  const collectTreeNodes = (rootId: string): string[] => {
    const component: string[] = [];
    const queue: string[] = [rootId];
    const localVisited = new Set<string>();

    while (queue.length > 0) {
      const currentId = queue.shift()!;

      if (localVisited.has(currentId)) continue;
      localVisited.add(currentId);
      component.push(currentId);

      const children = positions.filter(
        (pos) =>
          pos.flair__Parent_Position__c === currentId &&
          !localVisited.has(pos.Id),
      );

      children.forEach((child) => {
        if (!localVisited.has(child.Id)) {
          queue.push(child.Id);
        }
      });
    }

    return component;
  };

  positions.forEach((pos) => {
    if (!visited.has(pos.Id)) {
      const rootId = findTreeRoot(pos.Id);
      const component = collectTreeNodes(rootId);

      component.forEach((id) => visited.add(id));

      if (component.length > 0) {
        treeComponents.push(component);
      }
    }
  });

  const validComponents = treeComponents.filter(
    (component) => component.length > 0,
  );

  return validComponents;
};

export const getSmartFilteredPositions = (
  allPositions: EmployeePosition[],
  currentPositionId: string,
) => {
  const treeComponents = buildPositionTree(allPositions, currentPositionId);
  const filteredPositionIds = new Set<string>();

  treeComponents.forEach((tree) => {
    const treePositions = allPositions.filter((pos) => tree.includes(pos.Id));
    const hasUnstaffedPosition = treePositions.some(
      (pos) => !pos.flair__Staffed__c,
    );
    const containsCurrentPosition = tree.includes(currentPositionId);

    if (hasUnstaffedPosition || containsCurrentPosition) {
      tree.forEach((posId) => filteredPositionIds.add(posId));
    }
  });

  return allPositions.filter((pos) => filteredPositionIds.has(pos.Id));
};

export const getRelevantPositionsForEmployee = (
  positions: EmployeePosition[],
  currentPosition: EmployeePosition,
) => {
  const relevantIds = new Set<string>([currentPosition.Id]);

  let parentId = currentPosition.flair__Parent_Position__c;
  for (let i = 0; i < 2 && parentId; i++) {
    const currentParentId = parentId;
    const parent = positions.find((p) => p.Id === currentParentId);
    if (parent) {
      relevantIds.add(parent.Id);
      parentId = parent.flair__Parent_Position__c;
    } else {
      break;
    }
  }

  const directChildren = positions.filter(
    (pos) => pos.flair__Parent_Position__c === currentPosition.Id,
  );
  directChildren.forEach((child) => relevantIds.add(child.Id));

  if (currentPosition.flair__Parent_Position__c) {
    const siblings = positions.filter(
      (pos) =>
        pos.flair__Parent_Position__c ===
          currentPosition.flair__Parent_Position__c &&
        pos.Id !== currentPosition.Id,
    );
    siblings.slice(0, 2).forEach((sibling) => relevantIds.add(sibling.Id));
  }

  directChildren.forEach((child) => {
    const grandchildren = positions.filter(
      (pos) => pos.flair__Parent_Position__c === child.Id,
    );
    grandchildren
      .slice(0, 1)
      .forEach((grandchild) => relevantIds.add(grandchild.Id));
  });

  return positions.filter((pos) => relevantIds.has(pos.Id)).slice(0, 10);
};

export const getFullTreeForEmployee = (
  positions: EmployeePosition[],
  currentPosition: EmployeePosition,
): EmployeePosition[] => {
  const treeComponents = buildPositionTree(positions, currentPosition.Id);

  const currentTreeComponent = treeComponents.find((component) =>
    component.includes(currentPosition.Id),
  );

  if (!currentTreeComponent) {
    return [currentPosition];
  }

  return positions.filter((pos) => currentTreeComponent.includes(pos.Id));
};
